import React, { useEffect, useMemo, useState } from "react";
import { Loader2, MessageCircle, Users, TrendingUp, Bot, Download } from "lucide-react";
import api from "@/lib/api";
import { getApiBase } from "@/lib/api";

export function AdminSupportAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/support/analytics?days=${days}`);
      setData(r.data);
    } catch (_) {
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [days]);

  const maxDaily = useMemo(() => Math.max(1, ...(data?.daily || []).map((d) => d.messages)), [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="support-analytics-loading">
        <Loader2 className="w-6 h-6 animate-spin text-electric" />
      </div>
    );
  }

  if (!data) {
    return <div className="text-slate-400 text-sm">Nincs adat.</div>;
  }

  return (
    <div className="space-y-6" data-testid="support-analytics-panel">
      {/* Time range selector */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-slate-400">Időszak:</span>
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            data-testid={`support-analytics-range-${d}`}
            onClick={() => setDays(d)}
            className={`px-3 py-1 rounded-full text-xs border transition-colors ${
              days === d ? "bg-electric text-white border-electric" : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06]"
            }`}
          >
            {d} nap
          </button>
        ))}
        <div className="flex-1" />
        <button
          data-testid="support-analytics-export-csv"
          onClick={async () => {
            try {
              const token = localStorage.getItem("autopark_token");
              const r = await fetch(`${getApiBase()}/admin/support/analytics/export?days=${days}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (!r.ok) throw new Error("export failed");
              const blob = await r.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `autopark-support-top-${days}d.csv`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
            } catch (_) {
              // silent
            }
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 text-slate-300"
        >
          <Download className="w-3.5 h-3.5" /> CSV export
        </button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard testid="support-total-messages" icon={<MessageCircle className="w-4 h-4 text-electric" />} label="Összes üzenet" value={data.total_messages} />
        <StatCard testid="support-unique-sessions" icon={<Users className="w-4 h-4 text-electric" />} label="Egyedi sessionök" value={data.unique_sessions} />
        <StatCard testid="support-authed-messages" icon={<Bot className="w-4 h-4 text-electric" />} label="Bejelentkezett" value={data.authed_messages} />
        <StatCard testid="support-anon-messages" icon={<TrendingUp className="w-4 h-4 text-electric" />} label="Anonim" value={data.anon_messages} />
      </div>

      {/* Daily bar chart */}
      <div className="glass rounded-2xl p-5" data-testid="support-daily-chart">
        <div className="text-sm text-slate-400 mb-4">Napi üzenetek — utolsó {data.period_days} nap</div>
        {data.daily.length === 0 ? (
          <div className="text-xs text-slate-500 py-8 text-center">Nincs adat az időszakban.</div>
        ) : (
          <div className="flex items-end gap-1 h-40">
            {data.daily.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group" title={`${d.date}: ${d.messages} üzenet / ${d.sessions} session`}>
                <div className="w-full bg-electric/70 hover:bg-electric rounded-t transition-colors" style={{ height: `${(d.messages / maxDaily) * 100}%` }} />
                <div className="text-[9px] text-slate-500 rotate-45 origin-left translate-y-2 whitespace-nowrap">{d.date.slice(5)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top questions */}
      <div className="glass rounded-2xl p-5" data-testid="support-top-questions">
        <div className="text-sm text-slate-400 mb-4">Leggyakoribb kérdések (min. 2 előfordulás)</div>
        {data.top_questions.length === 0 ? (
          <div className="text-xs text-slate-500 py-4 text-center">Még nincs ismétlődő kérdés.</div>
        ) : (
          <div className="space-y-2">
            {data.top_questions.map((q, i) => (
              <div key={i} data-testid={`support-top-question-${i}`} className="flex items-center gap-3 p-3 bg-white/[0.02] rounded-lg border border-white/5">
                <div className="text-xs text-electric font-mono w-6">#{i + 1}</div>
                <div className="flex-1 text-sm text-slate-200 line-clamp-1">{q.question}</div>
                <div className="text-xs text-slate-400 shrink-0">{q.count}×</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent messages */}
      <div className="glass rounded-2xl p-5" data-testid="support-recent-messages">
        <div className="text-sm text-slate-400 mb-4">Legutóbbi kérdések</div>
        <div className="space-y-3">
          {data.recent.map((r, i) => (
            <div key={i} data-testid={`support-recent-${i}`} className="p-3 bg-white/[0.02] rounded-lg border border-white/5 space-y-1">
              <div className="flex items-center justify-between text-[10px] text-slate-500">
                <span>{r.user_email || `anon:${(r.session_id || '').slice(0, 8)}`}</span>
                <span>{new Date(r.created_at).toLocaleString("hu-HU")}</span>
              </div>
              <div className="text-sm text-slate-200">→ {r.user_text}</div>
              <div className="text-xs text-slate-400 line-clamp-2 pl-3 border-l-2 border-electric/40">{r.bot_reply}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ testid, icon, label, value }) {
  return (
    <div data-testid={testid} className="glass rounded-2xl p-4">
      <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">{icon}{label}</div>
      <div className="font-heading text-2xl font-light">{value}</div>
    </div>
  );
}
